import { useState, useEffect } from "react";

export const TWO_FA_DURATIONS = {
  FIRST_CALL: 2 * 60 + 5, // 125 secondes
  SECOND_CALL: 3 * 60, // 180 secondes
  PIN_EXPIRATION: 5 * 60, // 300 secondes
} as const;

export type TimerState = {
  firstCall: number
  secondCall: number
  pinExpiration: number
};

const calculateTimers = (timestampInSeconds: number): TimerState => {
  const nowInSeconds = Math.floor(Date.now() / 1000);
  const elapsedTimeInSeconds = Math.floor((nowInSeconds - timestampInSeconds));

  const { FIRST_CALL, SECOND_CALL, PIN_EXPIRATION } = TWO_FA_DURATIONS;

  if (elapsedTimeInSeconds < FIRST_CALL) {
    return {
      firstCall: Math.max(0, FIRST_CALL - elapsedTimeInSeconds),
      secondCall: SECOND_CALL,
      pinExpiration: PIN_EXPIRATION,
    };
  }

  if (elapsedTimeInSeconds < FIRST_CALL + SECOND_CALL) {
    const secondCallEndTime = FIRST_CALL + SECOND_CALL;
    return {
      firstCall: 0,
      secondCall: Math.max(0, secondCallEndTime - elapsedTimeInSeconds),
      pinExpiration: PIN_EXPIRATION,
    };
  }

  if (elapsedTimeInSeconds < FIRST_CALL + SECOND_CALL + PIN_EXPIRATION) {
    const pinExpirationEndTime = FIRST_CALL + SECOND_CALL + PIN_EXPIRATION;
    return {
      firstCall: 0,
      secondCall: 0,
      pinExpiration: Math.max(0, pinExpirationEndTime - elapsedTimeInSeconds),
    };
  }

  return { firstCall: 0, secondCall: 0, pinExpiration: 0 };
};

export const useTwoFaTimers = (timestamp: number) => {
  const [timers, setTimers] = useState<TimerState>(calculateTimers(timestamp));

  useEffect(() => {
    const interval = setInterval(() => setTimers(calculateTimers(timestamp)), 1000);
    return () => clearInterval(interval);
  }, [timestamp]);

  return timers;
};
